import {PredictionResponse} from '@mira/api';
import {
  CATEGORY_TO_PARTS,
  VARIANT_PRIORITY,
  type CarDiagramData,
  type DiagramVariant,
} from '@mira/ui';
import {match} from 'ts-pattern';

import {useMemo} from 'react';

import {forEach, forEachObjIndexed} from 'ramda';

import type {Nullish} from 'shared/web';

import {ROW_TYPE_THRESHOLDS} from '../constants/categoryMapping';
import {REPORT_WINDOW_KEY} from '../constants/reportWindow';

export const DEFAULT_WINDOW_KEY = REPORT_WINDOW_KEY;

export const EMPTY_DATA: CarDiagramData = {
  exterior: {},
  interior: {},
  mechanics: {},
  engine: {},
  electric: {},
};

/**
 * Maps an API prediction to a diagram variant.
 *
 * Highlight policy (binary on/off, no visual confidence treatment):
 * - `willNeed=true`  → always highlight, severity by confidence
 *   (`damage` ≥ 80%, `service` ≥ 50%, otherwise `suggestions`)
 * - `willNeed=false` + confidence < 80% → highlight as `suggestions`
 *   (model is uncertain → worth attention)
 * - `willNeed=false` + confidence ≥ 80% → `default` (model is confident
 *   no action is needed → don't clutter the diagram)
 */
export function getCategoryVariant(willNeed: boolean, confidence: number): DiagramVariant {
  return match({willNeed, confidence})
    .returnType<DiagramVariant>()
    .with({willNeed: false}, (p) =>
      p.confidence >= ROW_TYPE_THRESHOLDS.DAMAGE ? 'default' : 'suggestions'
    )
    .when(
      (p) => p.confidence >= ROW_TYPE_THRESHOLDS.DAMAGE,
      () => 'damage'
    )
    .when(
      (p) => p.confidence >= ROW_TYPE_THRESHOLDS.SERVICE,
      () => 'service'
    )
    .otherwise(() => 'suggestions');
}

export function applyVariantWithPriority(
  current: DiagramVariant | undefined,
  incoming: DiagramVariant
): DiagramVariant {
  return match(current)
    .with(undefined, () => incoming)
    .otherwise((cur) => (VARIANT_PRIORITY[incoming] > VARIANT_PRIORITY[cur] ? incoming : cur));
}

/**
 * Transforms prediction API data into CarDiagramData format for the car diagram component.
 * Uses the v2 default window (`window_5_1`) unless `windowKey` is overridden.
 *
 * @param predictionResponse - API response from predictByVin
 * @param windowKey - Prediction window to use (defaults to `REPORT_WINDOW_KEY`)
 */
export function useCarDiagramData(
  predictionResponse: PredictionResponse | Nullish,
  windowKey: string = DEFAULT_WINDOW_KEY
): CarDiagramData {
  return useMemo(() => {
    if (predictionResponse == null) {
      return EMPTY_DATA;
    }

    const windowPredictions = predictionResponse.predictions[windowKey];

    if (windowPredictions == null) {
      return EMPTY_DATA;
    }

    const exterior: Record<string, DiagramVariant> = {};
    const interior: Record<string, DiagramVariant> = {};
    const mechanics: Record<string, DiagramVariant> = {};
    const engine: Record<string, DiagramVariant> = {};
    const electric: Record<string, DiagramVariant> = {};

    const viewMap: Record<string, Record<string, DiagramVariant>> = {
      exterior,
      interior,
      mechanics,
      engine,
      electric,
    };

    forEachObjIndexed((prediction, categoryId) => {
      const mapping = CATEGORY_TO_PARTS[categoryId];

      if (mapping == null) {
        return;
      }

      const variant = getCategoryVariant(prediction.will_need, prediction.confidence);
      const targetView = viewMap[mapping.view];

      if (targetView == null) {
        return;
      }

      if (variant === 'default') {
        return;
      }

      forEach((partId: string) => {
        targetView[partId] = applyVariantWithPriority(targetView[partId], variant);
      }, mapping.parts);
    }, windowPredictions);

    return {
      exterior,
      interior,
      mechanics,
      engine,
      electric,
    };
  }, [predictionResponse, windowKey]);
}
